/* AOP Budget page chrome styles — extracted out of AOPBudget.js so that file
   stays focused on state/behavior. Identical convention to Monthly DFR /
   Waterfall EAC's own inline page styles, just split into its own module. */
import { makeStyles } from "@material-ui/core";
import { REPORT_FONT } from "../../reportFonts";
import { TH_BG_BAND } from "../../reportTableConfig";
import { PL_PAGE_BG } from "../pl-by-program/plByProgramTheme";

export const TAB_ACTIVE = "#4F46E5";
export const BORDER = "#E5E7EB";

export const useAOPBudgetStyles = makeStyles(() => ({
  page: {
    fontFamily: REPORT_FONT,
    backgroundColor: PL_PAGE_BG,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: TAB_ACTIVE,
    background: TAB_ACTIVE,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  tabBar: {
    background: "#fff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 700,
    borderBottom: `2px solid ${TAB_ACTIVE}`,
  },
  headerContainer: { margin: "20px 0 0", padding: "0 16px" },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: { flex: 1, padding: "0 16px", overflowY: "auto", minWidth: 0 },

  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${TAB_ACTIVE}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
    "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: TAB_ACTIVE, borderColor: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
}));
